import React, { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { FinancialAidStaffDetailContext } from '../ApplicantDetail';
import { asArray, asRecord } from '../../utils/domainData';
import {
  firstNonEmptyRawText,
  readParentCollectedFinancialAidSnapshot,
  readRawAmount,
  readSnapshotRecord,
} from './staffFinancialAidRawSnapshot';
import styles from './AssetsTab.module.css';

const readText = (value: unknown, fallback = ''): string => {
  const next = String(value ?? '').trim();
  return next || fallback;
};

const asNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

interface CardProps {
  title: string;
  children: React.ReactNode;
}

const Card: React.FC<CardProps> = ({ title, children }) => (
  <div className={styles.assetCard} data-testid="financial-aid-assets-card">
    <h3 className={styles.assetTitle}>{title}</h3>
    <div className={styles.assetRows}>
      {children}
    </div>
  </div>
);

interface RowProps {
  label: string;
  value: string;
  highlight?: boolean;
}

const Row: React.FC<RowProps> = ({ label, value, highlight = false }) => (
  <div className={styles.assetRow} data-testid="financial-aid-assets-row">
    <span className={styles.assetLabel}>{label}</span>
    <span className={`${styles.assetValue} ${highlight ? styles.assetValueHighlight : ''}`}>{value}</span>
  </div>
);

type AssetsEquityView = {
  primaryResidence: {
    estimatedValue: number;
    mortgageBalance: number;
    calculatedEquity: number;
    adminNote: string;
  };
  liquidAssets: {
    savings: number;
    checking: number;
  };
  vehicles: Array<{
    description: string;
    value: number;
  }>;
};

const formatRawCurrency = (amount: number): string => amount.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const buildRawResidenceNote = (rawAssets: Record<string, unknown>): string => {
  const parts = [
    firstNonEmptyRawText(rawAssets.purchaseDate) ? `Purchased ${firstNonEmptyRawText(rawAssets.purchaseDate)}` : '',
    readRawAmount(rawAssets.mortgagePayment) > 0 ? `Mortgage payment ${formatRawCurrency(readRawAmount(rawAssets.mortgagePayment))}` : '',
    firstNonEmptyRawText(rawAssets.mortgageYears) ? `Mortgage term ${firstNonEmptyRawText(rawAssets.mortgageYears)} years` : '',
    readRawAmount(rawAssets.propertyTaxes) > 0 ? `Property taxes ${formatRawCurrency(readRawAmount(rawAssets.propertyTaxes))}` : '',
    firstNonEmptyRawText(rawAssets.interestRate) ? `Interest rate ${firstNonEmptyRawText(rawAssets.interestRate)}%` : '',
  ].filter(Boolean);

  return parts.join(' | ');
};

export const buildAssetsEquity = (review: FinancialAidStaffDetailContext['review']): AssetsEquityView => {
  const reviewRecord = asRecord(review as unknown);
  const application = asRecord(reviewRecord.application);
  const sections = asRecord(application.sections);

  const assets = asRecord(sections.assets);
  const realEstate = asRecord(sections.realEstate);
  const properties = asArray<Record<string, unknown>>(realEstate.properties);
  const rawSnapshot = readParentCollectedFinancialAidSnapshot(review);
  const rawHomeAssets = readSnapshotRecord(rawSnapshot, 'assets');
  const rawSavings = readSnapshotRecord(rawSnapshot, 'savings');
  const rawChecking = readSnapshotRecord(rawSnapshot, 'checking');

  const primaryResidence = properties.find((property) =>
    readText(property.propertyType).toLowerCase() === 'primary_residence'
  ) ?? properties[0] ?? {};

  const estimatedValue = asNumber(primaryResidence.currentValue)
    || readRawAmount(rawHomeAssets.currentValue)
    || readRawAmount(rawHomeAssets.purchasePrice);
  const mortgageBalance = asNumber(primaryResidence.mortgageBalance)
    || readRawAmount(rawHomeAssets.mortgageBalance);
  const calculatedEquity = asNumber(primaryResidence.equity) || Math.max(estimatedValue - mortgageBalance, 0);

  const vehicleSection = asRecord(sections.vehicles);
  const vehicleEntries = asArray<Record<string, unknown>>(vehicleSection.vehicles);
  const vehicles = vehicleEntries.map((entry) => ({
    description: `${readText(entry.year)} ${readText(entry.make)} ${readText(entry.model)}`.replace(/\s+/g, ' ').trim() || 'Vehicle',
    value: asNumber(entry.estimatedValue),
  }));

  return {
    primaryResidence: {
      estimatedValue,
      mortgageBalance,
      calculatedEquity,
      adminNote: readText(primaryResidence.notes, buildRawResidenceNote(rawHomeAssets)),
    },
    liquidAssets: {
      savings: readRawAmount(rawSavings.balance)
        || asNumber(assets.checkingSavingsTotal)
        || asNumber(assets.cashOnHand),
      checking: readRawAmount(rawChecking.balance)
        || asNumber(assets.investmentsTotal)
        || asNumber(assets.retirementTotal),
    },
    vehicles,
  };
};

export const AssetsTab: React.FC = () => {
  const { review, layoutMode } = useOutletContext<FinancialAidStaffDetailContext>();
  const isCompactLayout = layoutMode === 'compact';

  const assetsEquity = useMemo(() => buildAssetsEquity(review), [review]);

  const formatCurrency = (val: number) =>
    val.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

  if (!review) {
    return <p className="text-sm text-[#6B7280]">Application data is syncing.</p>;
  }

  return (
    <div data-testid="financial-aid-assets-equity-tab">
      <div
        className={`${styles.assetsGrid} ${isCompactLayout ? styles.assetsGridCompact : ''}`}
        data-testid="financial-aid-assets-grid"
      >
        <Card title="PRIMARY RESIDENCE">
          <Row label="Value:" value={formatCurrency(assetsEquity.primaryResidence.estimatedValue)} />
          <Row label="Mortgage:" value={formatCurrency(assetsEquity.primaryResidence.mortgageBalance)} />
          <Row label="Equity:" value={formatCurrency(assetsEquity.primaryResidence.calculatedEquity)} highlight />
          <p className={styles.assetNote}>{assetsEquity.primaryResidence.adminNote || 'No note provided'}</p>
        </Card>

        <Card title="LIQUID ASSETS">
          <Row label="Savings:" value={formatCurrency(assetsEquity.liquidAssets.savings)} />
          <Row label="Checking:" value={formatCurrency(assetsEquity.liquidAssets.checking)} />
        </Card>

        <Card title="VEHICLES">
          {assetsEquity.vehicles.length === 0 ? (
            <p className={styles.emptyValue}>None reported</p>
          ) : (
            assetsEquity.vehicles.map((vehicle, index) => (
              <Row key={`${vehicle.description}-${index}`} label={vehicle.description} value={formatCurrency(vehicle.value)} />
            ))
          )}
        </Card>
      </div>
    </div>
  );
};
